import type { ReactNode, CSSProperties } from 'react';
import React from 'react';

interface Particle {
  startX: number;
  startY: number;
  x: number;
  y: number;
  angle: number;
  counter: number;
  increase: number;
  life: number;
  death: number;
  speed: number;
  size: number;
  opacity: number;
}

type ParticleEffectButtonType = 'circle' | 'rectangle' | 'triangle';
type ParticleEffectButtonStyle = 'fill' | 'stroke';
type ParticleEffectButtonDirection = 'left' | 'right' | 'top' | 'bottom';
type ParticleEffectButtonStatus = 'normal' | 'hidden' | 'hiding' | 'showing';

interface ParticleEffectItemProps {
  hidden: boolean;
  children: ReactNode;
  className?: string;
  duration?: number;
  easing?: string;
  type?: ParticleEffectButtonType;
  style?: ParticleEffectButtonStyle;
  direction?: ParticleEffectButtonDirection;
  canvasPadding?: number;
  size?: number | (() => number);
  speed?: number | (() => number);
  color?: string;
  particlesAmountCoefficient?: number;
  oscillationCoefficient?: number;
  onBegin?: () => void;
  onComplete?: () => void;
}

// eslint-disable-next-line react-refresh/only-export-components
export enum ParticleEffectEnum {
  Duration = 1000,
}

function rand(value: number): number {
  return Math.random() * value - value / 2;
}

function randIn(min: number, max: number): number {
  return Math.random() * (max - min) + min;
}

function isFunc<T>(value: unknown): value is () => T {
  return typeof value === 'function';
}

const defaultProps = {
  hidden: false,
  duration: ParticleEffectEnum.Duration,
  easing: 'easeInOutCubic',
  type: 'circle' as ParticleEffectButtonType,
  style: 'fill' as ParticleEffectButtonStyle,
  direction: 'left' as ParticleEffectButtonDirection,
  canvasPadding: 10,
  size: () => randIn(1, 3),
  speed: () => randIn(0.5, 2.5),
  color: '#000',
  particlesAmountCoefficient: 4,
  oscillationCoefficient: 8,
  onBegin: () => {},
  onComplete: () => {},
};

const ParticleEffectItem = (props: ParticleEffectItemProps) => {
  const {
    hidden,
    children,
    className,
    duration = defaultProps.duration,
    easing = defaultProps.easing,
    type = defaultProps.type,
    style = defaultProps.style,
    direction = defaultProps.direction,
    canvasPadding = defaultProps.canvasPadding,
    size = defaultProps.size,
    speed = defaultProps.speed,
    color = defaultProps.color,
    particlesAmountCoefficient = defaultProps.particlesAmountCoefficient,
    oscillationCoefficient = defaultProps.oscillationCoefficient,
    onBegin = defaultProps.onBegin,
    onComplete = defaultProps.onComplete,
  } = props;

  const [status, setStatus] = useState<ParticleEffectButtonStatus>(
    hidden ? 'hidden' : 'normal'
  );
  const [progress, setProgress] = useState(0);

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const ctxRef = useRef<CanvasRenderingContext2D | null>(null);
  const rectRef = useRef<DOMRect | null>(null);
  const progressRef = useRef(0);
  const particlesRef = useRef<Particle[]>([]);
  const animationFrameIdRef = useRef<number | null>(null);
  const startTimeRef = useRef<number | null>(null);

  const isHorizontal = () => {
    return direction === 'left' || direction === 'right';
  };

  const updateStatus = useCallback(() => {
    if (hidden) {
      if (status !== 'hiding' && status !== 'hidden') {
        setStatus('hiding');
      }
    } else {
      if (status !== 'showing' && status !== 'normal') {
        setStatus('showing');
      }
    }
  }, [hidden, status]);

  const cycleStatus = () => {
    if (animationFrameIdRef.current) {
      cancelAnimationFrame(animationFrameIdRef.current);
      animationFrameIdRef.current = null;
    }

    if (status === 'normal') {
      setStatus('hiding');
    } else if (status === 'hidden') {
      setStatus('showing');
    } else if (status === 'hiding') {
      setStatus('hidden');
    } else if (status === 'showing') {
      setStatus('normal');
    }
  };

  const startAnimation = () => {
    if (animationFrameIdRef.current) {
      cancelAnimationFrame(animationFrameIdRef.current);
      animationFrameIdRef.current = null;
    }

    if (!canvasRef.current || !wrapperRef.current) return;

    if (status === 'hiding') {
      progressRef.current = 0;
    } else {
      progressRef.current = 1;
    }

    particlesRef.current = [];
    rectRef.current = wrapperRef.current.getBoundingClientRect();

    const canvas = canvasRef.current;
    // canvas.width = rectRef.current.width + canvasPadding * 2;
    canvas.width = rectRef.current.width;
    canvas.height = rectRef.current.height + canvasPadding * 2;

    ctxRef.current = canvas.getContext('2d');
    // canvas.style.width = rectRef.current.width + 'px';
    canvas.style.height = rectRef.current.height + canvasPadding * 2 + 'px';

    if (onBegin) {
      onBegin();
    }

    startTimeRef.current = performance.now();
    animationFrameIdRef.current = requestAnimationFrame(animationLoop);
  };

  const animationLoop = (timestamp: number) => {
    try {
      if (!startTimeRef.current) startTimeRef.current = timestamp;

      const elapsed = timestamp - startTimeRef.current;
      const progressValue = Math.min(elapsed / duration, 1);

      let normalizedProgress;
      if (easing === 'easeInOutCubic') {
        if (progressValue < 0.5) {
          normalizedProgress = 4 * progressValue * progressValue * progressValue;
        } else {
          const f = progressValue - 1;
          normalizedProgress = 1 + 4 * f * f * f;
        }
      } else {
        normalizedProgress = progressValue;
      }

      const value =
        status === 'hiding' ? normalizedProgress * 100 : (1 - normalizedProgress) * 100;

      setProgress(value);

      if (duration) {
        addParticles(value / 100);
      }

      updateParticles();
      renderParticles();

      if (progressValue < 1) {
        animationFrameIdRef.current = requestAnimationFrame(animationLoop);
      } else {
        animationFrameIdRef.current = null;
        cycleStatus();
        if (onComplete) {
          onComplete();
        }
      }
    } catch (error) {
      console.error('Animation error:', error);
      animationFrameIdRef.current = null;
    }
  };

  const addParticles = (progress: number) => {
    if (!rectRef.current) return;

    const { width, height } = rectRef.current;
    const delta =
      status === 'hiding'
        ? progress - progressRef.current
        : progressRef.current - progress;

    const horizontal = isHorizontal();

    const progressValue =
      (horizontal ? width : height) * progress +
      delta * (status === 'hiding' ? 50 : 100);

    progressRef.current = progress;

    let x = canvasPadding;
    let y = canvasPadding;

    if (horizontal) {
      x += direction === 'left' ? progressValue : width - progressValue;
    } else {
      y += direction === 'top' ? progressValue : height - progressValue;
    }

    let i = Math.floor(particlesAmountCoefficient * 0.8 * (delta * 100 + 1));

    if (i > 0) {
      while (i--) {
        addParticle({
          x: x + (horizontal ? 0 : width * Math.random() * 0.6),
          y: y + (horizontal ? height * Math.random() * 0.6 : 0),
        });
      }
    }
  };

  const addParticle = (opts: { x: number; y: number }) => {
    const frames = (duration * 60) / 1000;

    const speedMultiplier = 0.7;
    const particleSpeed = (isFunc<number>(speed) ? speed() : speed) * speedMultiplier;
    const particleSize = isFunc<number>(size) ? size() : size;

    particlesRef.current.push({
      startX: opts.x,
      startY: opts.y,
      x: status === 'hiding' ? 0 : particleSpeed * -frames * 0.7,
      y: 0,
      angle: rand(180),
      counter: status === 'hiding' ? 0 : frames,
      increase: (Math.PI * 2) / 120,
      life: 0,
      death: status === 'hiding' ? frames - 10 + Math.random() * 20 : frames,
      speed: particleSpeed,
      size: particleSize,
      opacity: 1,
    });
  };

  const updateParticles = () => {
    for (let i = 0; i < particlesRef.current.length; i++) {
      const p = particlesRef.current[i];

      if (p.life > p.death) {
        particlesRef.current.splice(i, 1);
        i--;
      } else {
        p.x += p.speed * 0.85;

        const oscillationMultiplier = 0.8;
        p.y =
          oscillationCoefficient *
          oscillationMultiplier *
          Math.sin(p.counter * p.increase);

        p.life++;
        p.counter += status === 'hiding' ? 1 : -1;
      }
    }
  };

  const renderParticles = () => {
    if (!ctxRef.current || !canvasRef.current) return;

    const ctx = ctxRef.current;
    const canvas = canvasRef.current;

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    ctx.fillStyle = color;
    ctx.strokeStyle = color;

    for (let i = 0; i < particlesRef.current.length; ++i) {
      const p = particlesRef.current[i];

      if (p.life < p.death) {
        ctx.save();
        ctx.translate(p.startX, p.startY);
        ctx.rotate((p.angle * Math.PI) / 180);

        ctx.globalAlpha = status === 'hiding' ? 1 - p.life / p.death : p.life / p.death;

        ctx.beginPath();

        if (type === 'circle') {
          ctx.arc(p.x, p.y, p.size, 0, 2 * Math.PI);
        } else if (type === 'triangle') {
          ctx.moveTo(p.x, p.y);
          ctx.lineTo(p.x + p.size, p.y + p.size);
          ctx.lineTo(p.x + p.size, p.y - p.size);
          ctx.closePath();
        } else if (type === 'rectangle') {
          ctx.rect(p.x, p.y, p.size, p.size);
        }

        if (style === 'fill') {
          ctx.fill();
        } else if (style === 'stroke') {
          ctx.closePath();
          ctx.stroke();
        }

        ctx.globalAlpha = 1;
        ctx.restore();
      }
    }
  };

  const wrapperStyles: CSSProperties = {
    // position: 'relative',
    overflowX: 'hidden',
  };

  const canvasStyles: CSSProperties = {
    position: 'absolute',
    top: `-${canvasPadding}px`,
    left: '0',
    width: '100%',
    height: '100%',
    zIndex: 1,
    pointerEvents: 'none',
    borderRadius: '12px',
    // background: 'rgba(211, 106, 106, 0.5)',
  };

  if (status === 'hiding' || status === 'showing') {
    const prop = isHorizontal() ? 'translateX' : 'translateY';
    const size = rectRef.current
      ? isHorizontal()
        ? rectRef.current.width
        : rectRef.current.height
      : 0;
    const value = direction === 'left' || direction === 'top' ? progress : -progress;
    const px = Math.ceil((size * value) / 100);

    wrapperStyles.transform = `${prop}(${px}px)`;

    wrapperStyles.willChange = 'transform';
    wrapperStyles.borderRadius = '12px';
  } else if (status === 'hidden') {
    wrapperStyles.visibility = 'hidden';
    canvasStyles.visibility = 'hidden';
  } else if (status === 'normal') {
    canvasStyles.visibility = 'hidden';
  }

  const applyReverseTransform = (child: ReactNode): ReactNode => {
    if (!child || typeof child !== 'object' || !('props' in child)) {
      return child;
    }

    if (status !== 'hiding' && status !== 'showing') {
      return child;
    }

    const prop = isHorizontal() ? 'translateX' : 'translateY';
    const size = rectRef.current
      ? isHorizontal()
        ? rectRef.current.width
        : rectRef.current.height
      : 0;
    const value = direction === 'left' || direction === 'top' ? progress : -progress;
    const px = Math.ceil((size * value) / 100);

    const reverseTransform = `${prop}(${-px}px)`;

    const childStyle = child.props.style || {};
    const newStyle = {
      ...childStyle,
      transform: childStyle.transform
        ? `${childStyle.transform} ${reverseTransform}`
        : reverseTransform,
      willChange: 'transform',
    };

    return React.cloneElement(child, {
      ...child.props,
      style: newStyle,
    });
  };

  useEffect(() => {
    updateStatus();

    return () => {
      if (animationFrameIdRef.current) {
        cancelAnimationFrame(animationFrameIdRef.current);
        animationFrameIdRef.current = null;
      }
    };
  }, [hidden, status, updateStatus]);

  useEffect(() => {
    if (status === 'hiding' || status === 'showing') {
      startAnimation();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  useEffect(() => {
    return () => {
      if (animationFrameIdRef.current) {
        cancelAnimationFrame(animationFrameIdRef.current);
      }
    };
  }, []);

  return (
    <div
      className={className || 'particle-effect-item'}
      // style={{ position: 'relative', overflowX: 'hidden' }}
      style={{ position: 'relative' }}
    >
      <div className="particle-effect-content" ref={wrapperRef} style={wrapperStyles}>
        {React.Children.map(children, applyReverseTransform)}
      </div>
      <canvas ref={canvasRef} style={canvasStyles} />
    </div>
  );
};

export default ParticleEffectItem;
